import {
    type DashboardAccessInput,
    type DashboardSummary,
    type SharedDashboardSummary,
    getDashboard
} from '@/lib/dashboards-api';
import { relativeTime } from '@/lib/relativeTime';
import { cn } from '@/lib/utils';
import {
    ExternalLink,
    LayoutGrid,
    Lock,
    MessageSquare,
    Pencil,
    Trash2,
    X
} from 'lucide-react';
import { useEffect, useState } from 'react';
import useSWR from 'swr';

import { ExportToBigDialog } from '@/components/Dashboards/ExportToBigDialog';
import {
    exportedSummary,
    useDashboardExportState
} from '@/components/Dashboards/useDashboardExportState';
import { AgentDetailRow } from '@/components/agents/AgentDetailRow';
import { agentBadgeClassName } from '@/components/chat/MessageComposer/AgentBadges/AgentSourcesBadges';
import DashboardPreview from '@/components/chat/MessageComposer/DashboardPreview';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger
} from '@/components/ui/accordion';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';

interface ArtifactDetailPanelProps {
    dashboard: DashboardSummary | SharedDashboardSummary;
    onClose: () => void;
    hasAccess?: boolean;
    onOpen: () => void;
    onStartChat?: () => void;
    onDelete?: () => Promise<void>;
    onToggleShare?: (shared: boolean) => Promise<void>;
    onToggleListing?: (visible: boolean) => Promise<void>;
    onRename?: (title: string) => Promise<void>;
    onExportToApps?: (
        name: string,
        grouping: string,
        access: DashboardAccessInput | undefined
    ) => Promise<void>;
}

const formatDate = (value: string) =>
    new Date(value).toLocaleDateString(undefined, { dateStyle: 'medium' });

const formatDateTime = (value: string) =>
    new Date(value).toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short'
    });

function Field({ label, value }: { label: string; value: string }) {
    return (
        <AgentDetailRow label={label}>
            <p className="min-w-0 text-sm leading-5 truncate">{value}</p>
        </AgentDetailRow>
    );
}

function Section({
    title,
    count,
    aside,
    className,
    children
}: {
    title: string;
    count?: number;
    aside?: string;
    className?: string;
    children: React.ReactNode;
}) {
    return (
        <section className={cn('flex flex-col gap-2', className)}>
            <div className="flex justify-between items-baseline gap-2">
                {title && (
                    <h3 className="font-medium text-subtitle">
                        {title}
                        {count !== undefined && (
                            <span className="ml-1.5 font-normal text-muted-foreground">
                                {count}
                            </span>
                        )}
                    </h3>
                )}
                {aside && (
                    <span className="ml-auto text-caption text-muted-foreground">
                        {aside}
                    </span>
                )}
            </div>
            {children}
        </section>
    );
}

export function ArtifactDetailPanel({
    dashboard,
    onClose,
    hasAccess = true,
    onOpen,
    onStartChat,
    onDelete,
    onToggleShare,
    onToggleListing,
    onRename,
    onExportToApps
}: ArtifactDetailPanelProps) {
    const [confirmingDelete, setConfirmingDelete] = useState(false);
    const [confirmingUnshare, setConfirmingUnshare] = useState(false);
    const [actionError, setActionError] = useState<string | null>(null);
    const [exportOpen, setExportOpen] = useState(false);
    const [renaming, setRenaming] = useState(false);
    const [draft, setDraft] = useState('');

    const { data: detail } = useSWR(
        hasAccess ? ['dashboard', dashboard.id] : null,
        ([, id]) => getDashboard(id),
        { revalidateOnFocus: false, revalidateOnReconnect: false }
    );

    const { data: exportState, mutate: refreshExportState } =
        useDashboardExportState(dashboard.id, detail?.isOwner === true);
    const exportedText = exportedSummary(exportState);

    const confirmDelete = async () => {
        try {
            await onDelete?.();
        } catch (failure) {
            setActionError(
                failure instanceof Error ? failure.message : 'Could not delete'
            );
        }
    };

    const canRename = Boolean(onRename && detail?.isOwner);
    const startRename = () => {
        setDraft(dashboard.title);
        setRenaming(true);
    };

    const finishRename = async () => {
        const next = draft.trim();
        setRenaming(false);
        if (!next || next === dashboard.title) return;
        setActionError(null);
        try {
            await onRename?.(next);
        } catch (failure) {
            setActionError(
                failure instanceof Error ? failure.message : 'Could not rename'
            );
        }
    };

    const toggleListing = async (visible: boolean) => {
        setActionError(null);
        try {
            await onToggleListing?.(visible);
        } catch (failure) {
            setActionError(
                failure instanceof Error ? failure.message : 'Could not update'
            );
        }
    };

    const toggleShare = async (shared: boolean) => {
        setActionError(null);
        try {
            await onToggleShare?.(shared);
        } catch (failure) {
            setActionError(
                failure instanceof Error ? failure.message : 'Could not share'
            );
        }
    };

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (
                event.key === 'Escape' &&
                !confirmingDelete &&
                !confirmingUnshare
            )
                onClose();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [onClose, confirmingDelete, confirmingUnshare]);

    const ownerLabel =
        'ownerIdentifier' in dashboard
            ? dashboard.ownerIdentifier
            : detail?.isOwner
            ? 'you'
            : null;
    const connectorSummary = detail
        ? Array.from(new Set(detail.datasets.map((d) => d.connector))).join(
              ', '
          )
        : '';
    const canToggleShare = Boolean(onToggleShare && detail?.isOwner);
    const shareSwitch = (
        <Switch
            checked={Boolean(dashboard.sharedAt)}
            onCheckedChange={(shared) =>
                shared ? toggleShare(true) : setConfirmingUnshare(true)
            }
        />
    );

    return (
        <aside className="flex flex-col bg-background border-border border-l w-full lg:w-[420px] min-h-0 shrink-0 max-lg:fixed max-lg:inset-0 max-lg:z-40">
            <div className="flex justify-between items-center bg-background px-6 py-2 border-border border-b shrink-0">
                <span className="flex items-center gap-2 font-medium text-caption text-muted-foreground uppercase tracking-wider">
                    Dashboard
                    <span className="font-mono normal-case tracking-normal">
                        {dashboard.id.slice(0, 8)}
                    </span>
                </span>
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={onClose}
                    aria-label="Close"
                >
                    <X strokeWidth={1.5} />
                </Button>
            </div>

            <div className="flex flex-col flex-1 gap-5 p-6 min-h-0 overflow-y-auto custom-scrollbar [&>*]:shrink-0">
                <div className="flex flex-col gap-0 min-w-0">
                    {renaming ? (
                        <Input
                            autoFocus
                            value={draft}
                            maxLength={200}
                            onChange={(event) => setDraft(event.target.value)}
                            onBlur={finishRename}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') finishRename();
                                if (event.key === 'Escape') {
                                    event.stopPropagation();
                                    setRenaming(false);
                                }
                            }}
                            aria-label="Dashboard name"
                            className="-ml-2.5 h-9 font-medium text-lg leading-6"
                        />
                    ) : canRename ? (
                        <div className="group/title flex items-center gap-0.5 hover:bg-muted/60 -ml-2.5 rounded-md w-fit max-w-full min-w-0">
                            <Button
                                variant="ghost"
                                onClick={startRename}
                                title="Click to rename"
                                className="focus-visible:z-10 rounded-none rounded-l-md px-2.5 min-w-0 h-9 overflow-hidden font-medium text-foreground text-lg leading-6"
                            >
                                <span className="truncate">
                                    {dashboard.title}
                                </span>
                            </Button>
                            <div className="bg-border opacity-0 group-hover/title:opacity-100 group-focus-within/title:opacity-100 w-px h-6 transition-opacity duration-150" />
                            <Button
                                variant="ghost"
                                size="icon-sm"
                                onClick={startRename}
                                aria-label="Rename"
                                className="rounded-none rounded-r-md"
                            >
                                <Pencil />
                            </Button>
                        </div>
                    ) : (
                        <h2
                            title={dashboard.title}
                            className="font-medium text-lg leading-6 truncate"
                        >
                            {dashboard.title}
                        </h2>
                    )}
                    <p
                        className={cn(
                            'text-subtitle text-muted-foreground',
                            renaming || canRename ? '-mt-1' : 'mt-1'
                        )}
                    >
                        Updated {relativeTime(dashboard.updatedAt)} -{' '}
                        <span className="whitespace-nowrap">
                            Created {formatDate(dashboard.createdAt)}
                        </span>
                        {ownerLabel && (
                            <span className="whitespace-nowrap">
                                {' '}
                                by {ownerLabel}
                            </span>
                        )}
                    </p>
                </div>

                {hasAccess ? (
                    <DashboardPreview
                        key={dashboard.id}
                        dashboardId={dashboard.id}
                        className="shadow-none"
                    />
                ) : (
                    <div className="flex flex-col justify-center items-center gap-3 bg-[radial-gradient(hsl(var(--border))_1px,transparent_1px)] bg-muted/40 bg-[length:14px_14px] px-8 border border-border rounded-lg aspect-video text-center">
                        <span className="bg-background p-2.5 border border-border rounded-full text-muted-foreground">
                            <Lock className="size-5" strokeWidth={1.5} />
                        </span>
                        <div className="flex flex-col gap-0.5">
                            <p className="font-medium text-subtitle">
                                You do not have access to this dashboard.
                            </p>
                            <p className="text-subtitle text-muted-foreground">
                                Ask the owner or an admin for access.
                            </p>
                        </div>
                    </div>
                )}

                {(hasAccess || onStartChat) && (
                    <div className="flex gap-2">
                        {hasAccess && (
                            <Button onClick={onOpen} className="flex-1">
                                Open dashboard
                                <ExternalLink />
                            </Button>
                        )}
                        {onStartChat && (
                            <Button
                                variant="tertiary"
                                onClick={onStartChat}
                                className="flex-1"
                            >
                                <MessageSquare />
                                Chat about it
                            </Button>
                        )}
                    </div>
                )}

                <div className="flex flex-col">
                    {canToggleShare && (
                        <Section
                            title=""
                            aside={
                                dashboard.sharedAt
                                    ? `Shared ${relativeTime(
                                          dashboard.sharedAt
                                      )}`
                                    : undefined
                            }
                        >
                            <div className="flex flex-col divide-y divide-border">
                                <label className="flex justify-between items-center gap-3 py-4 cursor-pointer">
                                    <span className="min-w-0">
                                        <span className="block text-title">
                                            Show in Organization artifacts
                                        </span>
                                        <span className="block text-subtitle text-muted-foreground">
                                            {dashboard.sharedAt
                                                ? dashboard.visibleInLists
                                                    ? 'Only people with access can open it.'
                                                    : 'Hidden from people without access.'
                                                : 'Only visible to you.'}
                                        </span>
                                    </span>
                                    {shareSwitch}
                                </label>
                                {onToggleListing && dashboard.sharedAt && (
                                    <label className="flex justify-between items-center gap-3 py-4 cursor-pointer">
                                        <span className="min-w-0">
                                            <span className="block text-title">
                                                Show a locked card to others
                                            </span>
                                            <span className="block text-subtitle text-muted-foreground">
                                                People without access see the
                                                title, not the content.
                                            </span>
                                        </span>
                                        <Switch
                                            checked={Boolean(
                                                dashboard.visibleInLists
                                            )}
                                            onCheckedChange={toggleListing}
                                        />
                                    </label>
                                )}
                            </div>
                        </Section>
                    )}

                    {onExportToApps && detail?.isOwner && (
                        <div className="flex justify-between items-center gap-3 py-4 border-border border-t">
                            <span className="min-w-0">
                                <span className="block text-title">
                                    Export to BIG
                                </span>
                                <span className="block text-subtitle text-muted-foreground">
                                    {exportedText ??
                                        'Publish this dashboard as an app in BIG.'}
                                </span>
                            </span>
                            <Button
                                variant="tertiary"
                                onClick={() => {
                                    setExportOpen(true);
                                }}
                            >
                                <LayoutGrid />
                                {exportedText ? 'Update' : 'Export'}
                            </Button>
                        </div>
                    )}

                    <Accordion
                        type="multiple"
                        className="border-border border-t"
                    >
                        {hasAccess && (
                            <AccordionItem
                                value="sources"
                                className="border-border"
                            >
                                <AccordionTrigger className="py-4 font-normal text-title hover:no-underline [&>svg]:size-5">
                                    <span>
                                        Data sources
                                        {detail && (
                                            <span className="ml-1.5 font-normal text-muted-foreground">
                                                {detail.datasets.length}
                                            </span>
                                        )}
                                    </span>
                                    {connectorSummary && (
                                        <span className="mr-2 ml-auto font-normal text-subtitle text-muted-foreground truncate">
                                            {connectorSummary}
                                        </span>
                                    )}
                                </AccordionTrigger>
                                <AccordionContent className="pb-3">
                                    <ul className="flex flex-col divide-y divide-border">
                                        {detail
                                            ? detail.datasets.map((dataset) => (
                                                  <li
                                                      key={dataset.id}
                                                      className="items-start gap-4 grid grid-cols-[minmax(0,8rem)_minmax(0,1fr)] py-3 first:pt-0 last:pb-0"
                                                  >
                                                      <div
                                                          title={dataset.title}
                                                          className="py-2.5 font-medium text-sm leading-7 break-words line-clamp-3"
                                                      >
                                                          {dataset.title}
                                                      </div>
                                                      <div className="min-w-0">
                                                          <AgentDetailRow
                                                              label="Source"
                                                              alignToBadges
                                                          >
                                                              <Badge
                                                                  variant="secondary"
                                                                  className={
                                                                      agentBadgeClassName
                                                                  }
                                                              >
                                                                  {
                                                                      dataset.connector
                                                                  }
                                                              </Badge>
                                                          </AgentDetailRow>
                                                          <AgentDetailRow
                                                              label="Tool"
                                                              alignToBadges
                                                          >
                                                              <Badge
                                                                  variant="secondary"
                                                                  className={
                                                                      agentBadgeClassName
                                                                  }
                                                              >
                                                                  {dataset.tool}
                                                              </Badge>
                                                          </AgentDetailRow>
                                                      </div>
                                                  </li>
                                              ))
                                            : [0, 1].map((placeholder) => (
                                                  <li
                                                      key={placeholder}
                                                      className="py-3"
                                                  >
                                                      <Skeleton className="rounded-md w-full h-12" />
                                                  </li>
                                              ))}
                                    </ul>
                                </AccordionContent>
                            </AccordionItem>
                        )}
                        <AccordionItem
                            value="details"
                            className="border-border"
                        >
                            <AccordionTrigger className="py-4 font-normal text-title hover:no-underline [&>svg]:size-5">
                                <span>Details</span>
                                <span className="mr-2 ml-auto font-normal text-subtitle text-muted-foreground">
                                    Created {formatDate(dashboard.createdAt)}
                                </span>
                            </AccordionTrigger>
                            <AccordionContent className="pb-3">
                                <div>
                                    <Field
                                        label="Created"
                                        value={formatDateTime(
                                            dashboard.createdAt
                                        )}
                                    />
                                    <Field
                                        label="Updated"
                                        value={relativeTime(
                                            dashboard.updatedAt
                                        )}
                                    />
                                    {ownerLabel && (
                                        <Field
                                            label="Owner"
                                            value={
                                                'ownerIdentifier' in dashboard
                                                    ? ownerLabel
                                                    : detail?.isOwner
                                                    ? 'You'
                                                    : 'Shared with you'
                                            }
                                        />
                                    )}
                                    {dashboard.sharedAt && (
                                        <Field
                                            label="Shared"
                                            value={relativeTime(
                                                dashboard.sharedAt
                                            )}
                                        />
                                    )}
                                </div>
                            </AccordionContent>
                        </AccordionItem>
                    </Accordion>
                </div>

                {actionError && (
                    <p className="text-body text-destructive">{actionError}</p>
                )}
            </div>

            {detail?.isOwner && onDelete && (
                <div className="flex justify-end items-center px-6 py-3 border-border border-t shrink-0">
                    <Button
                        variant="ghost-destructive"
                        onClick={() => {
                            setActionError(null);
                            setConfirmingDelete(true);
                        }}
                    >
                        <Trash2 />
                        Delete
                    </Button>
                </div>
            )}
            {onExportToApps && (
                <ExportToBigDialog
                    open={exportOpen}
                    dashboardId={dashboard.id}
                    title={dashboard.title}
                    onOpenChange={setExportOpen}
                    onExport={async (name, grouping, access) => {
                        await onExportToApps(name, grouping, access);
                        await refreshExportState();
                    }}
                />
            )}
            <AlertDialog
                open={confirmingUnshare}
                onOpenChange={setConfirmingUnshare}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            Remove from Organization artifacts?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            “{dashboard.title}” disappears for everyone, and the
                            access you gave to people and groups is removed. If
                            you share it again, access has to be given again.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => toggleShare(false)}>
                            Remove
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
            <AlertDialog
                open={confirmingDelete}
                onOpenChange={setConfirmingDelete}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            Delete this dashboard?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            “{dashboard.title}” is removed for good. Chats that
                            show it will no longer be able to open it.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={confirmDelete}>
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </aside>
    );
}
